/**
 * useNotifications Hook
 * React hook for managing notifications on web platform
 */

import { useEffect, useState, useCallback } from 'react';
import type { NotificationPayload, NotificationPermissionStatus } from '@hemberedskapsappen/shared';
import { notificationService } from '@/services/NotificationService';
import { registerPushToken } from '@/services/api-client';

// Extend window interface to include expoPushToken from React Native WebView
declare global {
  interface Window {
    expoPushToken?: string;
    onPushTokenReceived?: (token: string) => void;
  }
}

export function useNotifications() {
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermissionStatus>({
    granted: false,
    canAskAgain: false,
  });
  const [isInitializing, setIsInitializing] = useState(false);

  // Initialize notification service on mount
  useEffect(() => {
    let mounted = true;

    async function init() {
      setIsInitializing(true);
      try {
        await notificationService.initialize();
        await notificationService.configureNotifications();
        
        if (mounted) {
          const status = await notificationService.getPermissionStatus();
          setPermissionStatus(status);
        }
      } catch (error) {
        console.error('[useNotifications] Failed to initialize:', error);
      } finally {
        if (mounted) {
          setIsInitializing(false);
        }
      }
    }

    init();

    // Listen for push token from React Native WebView
    // Note: Token registration happens in auth-store after login
    if (typeof window !== 'undefined') {
      window.onPushTokenReceived = async (token: string) => {
        console.log('[useNotifications] Received push token from native app');
        try {
          const result = await registerPushToken(token);
          if (result.success) {
            console.log('[useNotifications] Push token registered successfully');
          } else {
            console.error('[useNotifications] Failed to register push token:', result.error);
          }
        } catch (error) {
          console.error('[useNotifications] Error registering push token:', error);
        }
      };
    }

    return () => {
      mounted = false;
      if (typeof window !== 'undefined') {
        delete window.onPushTokenReceived;
      }
    };
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    const granted = await notificationService.requestPermission();
    const status = await notificationService.getPermissionStatus();
    setPermissionStatus(status);
    
    // Register push token if permission granted (web doesn't use Expo tokens)
    // Mobile apps will handle token registration separately
    
    return granted;
  }, []);

  // Send local notification
  const sendNotification = useCallback(async (payload: NotificationPayload) => {
    await notificationService.sendLocalNotification(payload);
  }, []);

  // Get push token (mobile only)
  const getPushToken = useCallback(() => {
    return notificationService.getExpoPushToken();
  }, []);

  return {
    permissionStatus,
    isInitializing,
    requestPermission,
    sendNotification,
    getPushToken,
  };
}
